import React, { useState, useEffect, useCallback } from "react";
import { Plus, Play, Pause, RotateCw, XCircle, Pencil } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SubscriptionStatusBadge, SubscriptionTypeBadge } from "./subscriptions/SubscriptionBadges";
import SubscriptionFormDialog from "./subscriptions/SubscriptionFormDialog";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function PlatformSubscriptions() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, t] = await Promise.all([
        api.get("/platform/subscriptions"),
        api.get("/platform/tenants"),
      ]);
      setItems(subs.data.items || []);
      setTenants(t.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb, body = {}) => {
    try {
      await api.post(`/platform/subscriptions/${id}/${verb}`, body);
      toast.success(`Subscription ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
  };

  const suspend = (id) => {
    const reason = window.prompt("Alasan suspend?");
    if (!reason) return;
    action(id, "suspend", { reason });
  };

  return (
    <PlatformLayout title="Subscriptions">
      <div data-testid="platform-subscriptions-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="text-xs text-white/50">Total: {items.length} subscription</div>
          <Button onClick={() => setDialog({ mode: "create" })} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-create-subscription">
            <Plus size={14} className="mr-1" /> Buat Subscription
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada subscription. Klik <strong className="text-white">+ Buat Subscription</strong>.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Tenant</div>
              <div className="col-span-1">Plan</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2">Period</div>
              <div className="col-span-1">Days Left</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((sub) => {
              const tenant = tenants.find((t) => t.tenant_id === sub.tenant_id);
              return (
                <div key={sub.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`sub-row-${sub.id}`}>
                  <div className="col-span-3 min-w-0">
                    <div className="text-sm font-bold text-white truncate">{tenant?.tenant_name || sub.tenant_id.slice(0, 8)}</div>
                    <div className="text-[10px] text-white/40 font-mono">{tenant?.tenant_code || "—"}</div>
                  </div>
                  <div className="col-span-1 text-xs text-white/70 font-black">{sub.plan_code}</div>
                  <div className="col-span-2"><SubscriptionTypeBadge type={sub.subscription_type} size="sm" /></div>
                  <div className="col-span-1"><SubscriptionStatusBadge status={sub.status} size="sm" /></div>
                  <div className="col-span-2 text-[11px] text-white/60">
                    {formatDate(sub.start_date)}<br />
                    <span className="text-white/40">→ {formatDate(sub.end_date)}</span>
                  </div>
                  <div className="col-span-1 text-xs">
                    {sub.remaining_days === null ? <span className="text-amber-400 font-bold">∞</span> :
                      <span className={sub.remaining_days < 7 ? "text-rose-400 font-bold" : "text-white/70"}>{sub.remaining_days}d</span>}
                  </div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <button onClick={() => setDialog({ mode: "edit", subscription: sub })}
                      className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="Edit" data-testid={`sub-edit-${sub.id}`}>
                      <Pencil size={12} />
                    </button>
                    {sub.status === "TRIAL" && (
                      <button onClick={() => action(sub.id, "activate")}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10"
                        title="Activate" data-testid={`sub-activate-${sub.id}`}><Play size={12} /></button>
                    )}
                    {(sub.status === "ACTIVE" || sub.status === "TRIAL") && (
                      <button onClick={() => suspend(sub.id)}
                        className="w-7 h-7 rounded-lg grid place-items-center text-amber-400 hover:bg-amber-500/10"
                        title="Suspend" data-testid={`sub-suspend-${sub.id}`}><Pause size={12} /></button>
                    )}
                    {sub.status === "SUSPENDED" && (
                      <button onClick={() => action(sub.id, "resume")}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10"
                        title="Resume" data-testid={`sub-resume-${sub.id}`}><RotateCw size={12} /></button>
                    )}
                    {sub.status !== "EXPIRED" && sub.subscription_type !== "LIFETIME" && (
                      <button onClick={() => window.confirm("Yakin expire subscription ini?") && action(sub.id, "expire")}
                        className="w-7 h-7 rounded-lg grid place-items-center text-rose-400 hover:bg-rose-500/10"
                        title="Expire" data-testid={`sub-expire-${sub.id}`}><XCircle size={12} /></button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <SubscriptionFormDialog
          mode={dialog?.mode || "create"}
          subscription={dialog?.subscription}
          tenants={tenants}
          open={!!dialog}
          onClose={() => setDialog(null)}
          onSaved={load}
        />
      </div>
    </PlatformLayout>
  );
}
